<template>
  <AppCard no-padding>
    <template #header>
      <div class="flex flex-1 items-center justify-between">
        <div
          class="text-navy-800 flex items-center gap-2 text-[13px] font-semibold"
        >
          <i class="pi pi-users text-navy-400 text-[13px]" />
          Members
          <span
            class="bg-navy-50 text-navy-500 min-w-[20px] rounded-full px-[7px] py-px text-center text-[11px] font-semibold"
          >
            {{ members.length }}
          </span>
        </div>
        <AppButton
          v-if="isAdmin"
          variant="ghost"
          size="sm"
          @click="$emit('invite')"
        >
          + Invite
        </AppButton>
      </div>
    </template>

    <!-- Search -->
    <div class="border-b border-neutral-100 px-5 py-2.5">
      <div class="relative">
        <i
          class="pi pi-search absolute top-1/2 left-2.5 -translate-y-1/2 text-xs text-neutral-400"
        />
        <input
          v-model="search"
          type="text"
          placeholder="Search members..."
          class="text-navy-800 w-full rounded-md border border-neutral-300 py-[7px] pr-3 pl-[30px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
        />
      </div>
    </div>

    <!-- Member list -->
    <template v-if="sortedMembers.length > 0">
      <div
        v-for="(member, i) in sortedMembers"
        :key="member.id"
        class="flex items-center justify-between px-5 py-2.5 transition-colors hover:bg-neutral-50"
        :class="{ 'border-b border-neutral-100': i < sortedMembers.length - 1 }"
      >
        <div class="flex items-center gap-2.5">
          <!-- Avatar -->
          <div
            class="from-navy-800 to-navy-700 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-[11px] font-bold tracking-wide text-white"
          >
            {{ initials(member.name) }}
          </div>
          <div class="min-w-0">
            <p class="text-navy-800 truncate text-[13px] font-medium">
              {{ member.name }}
            </p>
            <p class="truncate text-xs text-neutral-400">{{ member.email }}</p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Officer badge -->
          <span
            v-if="member.officer"
            class="shrink-0 rounded bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700"
          >
            Officer
          </span>

          <!-- Role badge -->
          <span
            :class="[
              'shrink-0 rounded px-2 py-0.5 text-[11px] font-medium',
              roleClasses(member.role),
            ]"
          >
            {{ member.role }}
          </span>

          <!-- Kebab menu -->
          <button
            v-if="isAdmin"
            class="rounded px-1 py-0.5 text-[13px] text-neutral-400 transition-colors hover:bg-neutral-100"
            @click="$emit('manageMember', member)"
          >
            <i class="pi pi-ellipsis-v text-xs" />
          </button>
        </div>
      </div>
    </template>

    <div v-else class="px-5 py-6 text-center text-[13px] text-neutral-500">
      No members found
    </div>
  </AppCard>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"

import type { MemberType } from "../../pages/Company/types"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"

interface Props {
  members: MemberType[]
  isAdmin: boolean
}

const props = defineProps<Props>()

defineEmits<{
  invite: []
  manageMember: [member: MemberType]
}>()

const search = ref("")

const ROLE_ORDER: Record<string, number> = {
  ADMIN: 0,
  Admin: 0,
  CONTRIBUTOR: 1,
  Contributor: 1,
  VIEWER: 2,
  Viewer: 2,
}

const sortedMembers = computed(() => {
  let list = props.members
  const q = search.value.toLowerCase().trim()
  if (q) {
    list = list.filter(
      (m) =>
        m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    )
  }
  return [...list].sort(
    (a, b) =>
      (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99) ||
      a.name.localeCompare(b.name),
  )
})

const initials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

const roleClasses = (role: string) => {
  const r = role.toUpperCase()
  if (r === "ADMIN") return "bg-blue-100 text-blue-800"
  if (r === "CONTRIBUTOR") return "bg-violet-100 text-violet-700"
  return "bg-neutral-100 text-neutral-500"
}
</script>
